import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastMessageService} from 'src/app/services/toast-message.service';
import {AddressType} from '../interface/address-type';
import {Address} from '../interface/address';
import {
	AddressTransmissionService
} from 'src/app/raintree/transmission/address-transmission/address-transmission.service';
import {RequestMapperService} from 'src/app/request-mapper.service';
import {AddressFunctionService} from '../service/address-function.service';
import {CountryList} from '../interface/countryList';
import {BloomsightService} from "../../bloomsight.service";
import {RaintreeResponse} from "../../raintree/interface/raintree-response";
import {CountryISO, PhoneNumberFormat, SearchCountryField} from "ngx-intl-tel-input";
import parsePhoneNumberFromString from "libphonenumber-js";
import {CurrencyResolverService} from "../../currency-resolver.service";

@Component({
	selector: 'fb-address-add',
	templateUrl: './address-add.component.html',
	styleUrls: ['./address-add.component.scss']
})
export class AddressAddComponent implements OnInit, OnChanges {

	@Input() addressTypeData: AddressType = {} as AddressType;
	@Input() addressData: Address = {} as Address;
	@Input() shippingAddressLength: number = 0;
	@Input() billingAddressLength: number = 0;
	@Output() addressSaved: EventEmitter<boolean> = new EventEmitter<boolean>();

	protected readonly SearchCountryField = SearchCountryField;
	protected readonly CountryISO = CountryISO;
	protected readonly PhoneNumberFormat = PhoneNumberFormat;

	public countryList: CountryList[] = [];
	public defaultCountry: CountryISO = CountryISO.India;
	public preferredCountries: CountryISO[] = [
		CountryISO.India,
		CountryISO.UnitedStates,
		CountryISO.UnitedKingdom,
		CountryISO.Canada,
		CountryISO.Australia,
		CountryISO.France,
		CountryISO.Denmark,
		CountryISO.Spain,
		CountryISO.Germany,
		CountryISO.Switzerland,
		CountryISO.Netherlands,
		CountryISO.Belgium,
		CountryISO.Israel,
		CountryISO.Japan,
		CountryISO.Greece,
		CountryISO.NewZealand
	];
	public addressFromData: FormGroup = this._fb.group({

		/*TODO: do not delete*/
		// name: ['test add name', Validators.required],
		// addressLineOne: ['test add address 1', Validators.required],
		// addressLineTwo: [''],
		// postalCode: ['700011', Validators.required],
		// city: ['test add city', Validators.required],
		// state: ['test add state', Validators.required],
		// country: ['India', Validators.required],
		// companyName: ['csdcds'],
		// primaryPhone: ['1234567890', Validators.required],
		// secondaryPhone: ['1234567890'],
		// contactEmail: ['aca@gmail.com', [Validators.required, Validators.email]],
		// vatgstNumber: ['casca'],
		// eoriNumber: ['cacacas'],
		// addressType: ['BILLING'], //outside
		// primaryBillingAddress: [false], //outside
		// primaryShippingAddress: [false], //outside
		// id: [0], //outside
		// version: [0], //outside

		name: ['', Validators.required],
		addressLineOne: ['', Validators.required],
		addressLineTwo: [''],
		postalCode: ['', Validators.required],
		city: ['', Validators.required],
		state: ['', Validators.required],
		country: ['', Validators.required],
		companyName: [''],
		primaryPhone: ['', Validators.required],
		secondaryPhone: [''],
		contactEmail: ['', [Validators.required, Validators.email]],
		vatgstNumber: [''],
		eoriNumber: [''],
		addressType: [''], //outside
		primaryBillingAddress: [false], //outside
		primaryShippingAddress: [false], //outside
		id: [0], //outside
		version: [0], //outside

	});

	constructor(
		public _toast: ToastMessageService,
		private _fb: FormBuilder,
		private _addressTransmission: AddressTransmissionService,
		private _addressService: AddressFunctionService,
		private _bloomsight: BloomsightService,
		private _currency: CurrencyResolverService
	) {
	}

	private hasCountryCode(phone: string): boolean {
		if(!phone) return false;
		const parsed = parsePhoneNumberFromString(phone);
		if(!parsed)
			return false;
		return parsed.isValid();
	}

	private setPhoneObject(phone: string): void {
		const parsed = parsePhoneNumberFromString(phone);

		if (parsed && parsed.isValid()) {
			const phoneObject = {
				number: parsed.nationalNumber,
				internationalNumber: parsed.formatInternational(),
				nationalNumber: parsed.formatNational(),
				e164Number: parsed.number,
				countryCode: parsed.country,
				dialCode: '+' + parsed.countryCallingCode
			};

			this.addressFromData.get('primaryPhone')?.setValue(phoneObject);
		}
	}

	public showCountryCodeError(): boolean {
		return this.addressFromData.controls['primaryPhone'].value && !this.hasCountryCode(this.addressData.primaryPhone) && !(this.addressTypeData.type === 'add');
	}

	public getCountryISO(name: string): CountryISO | undefined {
		return CountryISO[name.replace(/\s+/g, '') as keyof typeof CountryISO];
	}

	public submitAddressFromData(): void {

		if (!this.addressFromData.valid) {
			this._toast.error('fields are/is empty.', 'Please fill up all the required fields.');
			this.addressFromData.markAllAsTouched();
			return;
		}

		if (this.shippingAddressLength === 0 && !this.addressTypeData.isBilling) {
			this.addressFromData.patchValue({
				primaryShippingAddress: true,
			});
		}

		if (this.billingAddressLength === 0 && this.addressTypeData.isBilling) {
			this.addressFromData.patchValue({
				primaryBillingAddress: true,
			});
		}

		if (this.addressTypeData.type === 'add') this.addAddress();
		else this.updateAddress();

	}

	public ngOnInit(): void {
		this._addressService.fetchCountryList().subscribe((data: any) => {
			this.countryList = data;
		});

		this.defaultCountry = this.getCountryISO(this._currency.getShippingCountry()) || CountryISO.India;

		this.addressFromData.controls['country'].valueChanges.subscribe((data: any) => {
			this.defaultCountry = this.getCountryISO(data) || this.getCountryISO(this._currency.getShippingCountry()) || CountryISO.India;
		})
	}

	public addAddress(): void {

		let addressPostData = JSON.parse(JSON.stringify(this.addressFromData.getRawValue()));
		delete addressPostData.id;
		delete addressPostData.version;
		addressPostData.postalCode = addressPostData.postalCode + '';
		addressPostData.primaryPhone = (this.addressFromData.controls['primaryPhone'].value.e164Number + '').replace(/\s+/g, '');
		addressPostData.secondaryPhone = addressPostData.secondaryPhone + '';

		this._addressTransmission.AddAddres(
			RequestMapperService.ADD_ADDRESS,
			addressPostData,
			(): void => {
			},
			(): void => {
			},
			(response: RaintreeResponse): void => {
				addressPostData.id = +response.message;
				this._addressService.methSelectedAddress(addressPostData);
				this._toast.success('Address Added', 'Address updated Successfully');
				if(!this.addressTypeData.isBilling) {
					this._bloomsight.trackDataEvent(
						'6724797722fb296d61f67c05',
						[
							{email: addressPostData.contactEmail},
							{country: addressPostData.country},
							{state: addressPostData.state}
						],
						'Add shipping address'
					)
				}
				this.addressSaved.emit(false);
			},
			(error: string): void => {
				this._toast.error(error, error);
			},
			(): void => {
			}
		);
	}

	public updateAddress(): void {

		let addressPostData = JSON.parse(JSON.stringify(this.addressFromData.getRawValue()));
		delete addressPostData.version;
		addressPostData.postalCode = addressPostData.postalCode + '';
		addressPostData.primaryPhone = this.addressFromData.controls['primaryPhone'].value.e164Number + '';
		addressPostData.secondaryPhone = addressPostData.secondaryPhone + '';
		this._addressTransmission.updateAddres(
			RequestMapperService.UPDATE_ADDRESS,
			addressPostData,
			(): void => {
			},
			(): void => {
			},
			(): void => {
				this._addressService.methSelectedAddress(addressPostData);
				this._toast.success('Address updated', 'Address updated Successfully');
				this.addressSaved.emit(true);
			},
			(error: string): void => {
				this._toast.error(error, error);
			},
			(): void => {
			}
		)
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (changes['addressTypeData']['currentValue'].type === 'edit') {
			this.addressFromData.patchValue({
				name: this.addressData.name!,
				addressLineOne: this.addressData.addressLineOne!,
				addressLineTwo: this.addressData.addressLineTwo!,
				postalCode: this.addressData.postalCode!,
				city: this.addressData.city!,
				state: this.addressData.state!,
				country: this.addressData.country!,
				companyName: this.addressData.companyName ? this.addressData.companyName : '',
				primaryPhone: this.addressData.primaryPhone!,
				secondaryPhone: this.addressData.secondaryPhone ? this.addressData.secondaryPhone : '',
				contactEmail: this.addressData.contactEmail!,
				vatgstNumber: this.addressData.vatgstNumber ? this.addressData.vatgstNumber : '',
				eoriNumber: this.addressData.eoriNumber ? this.addressData.eoriNumber : '',
				addressType: this.addressData.addressType!,
				primaryBillingAddress: this.addressData.primaryBillingAddress,
				primaryShippingAddress: this.addressData.primaryShippingAddress,
				id: this.addressData.id,
				version: this.addressData.version,
			});
			this.setPhoneObject(this.addressData.primaryPhone);
		} else {
			this.addressFromData.patchValue({
				addressType: this.addressTypeData.isBilling ? 'BILLING' : 'SHIPPING',
			})
		}

	}
}
